import {
  ArrowDownToLine,
  Check,
  Download,
  Loader2,
  Plus,
  Scissors,
  SplitSquareHorizontal,
  Trash2,
  Upload,
} from "lucide-react"
import { useState } from "react"

import { FileDropzone } from "@/components/FileDropzone"
import { PdfThumbnail } from "@/components/PdfThumbnail"
import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { RelatedTools } from "@/components/related-tools"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useDocumentUpload } from "@/hooks/useDocumentUpload"
import { useOperationGuard } from "@/hooks/useOperationGuard"
import { usePdfSplit } from "@/hooks/usePdfSplit"
import { type PdfFile, usePdfThumbnails } from "@/hooks/usePdfThumbnails"

const howItWorks = [
  { icon: Upload, text: "Upload PDF" },
  { icon: SplitSquareHorizontal, text: "Choose pages" },
  { icon: ArrowDownToLine, text: "Download split files" },
]

interface Props {
  limits: {
    daily_remaining: number
    max_file_size_mb: number
    max_pages: number
  }
  authenticated: boolean
}

interface PageRange {
  id: string
  start: number
  end: number
}

export default function Split({ limits, authenticated }: Props) {
  const [file, setFile] = useState<PdfFile | null>(null)
  const [ranges, setRanges] = useState<PageRange[]>([])
  const [hasEditedRanges, setHasEditedRanges] = useState(false)
  const { generateThumbnails, loading: loadingThumbnails } = usePdfThumbnails()
  const { splitPdf, processing, progress } = usePdfSplit()
  const { uploadDocument } = useDocumentUpload()
  const {
    remainingOperations,
    completed,
    guardOperation,
    markCompleted,
    Modals,
  } = useOperationGuard({
    dailyRemaining: limits.daily_remaining,
    authenticated,
  })

  const handleFilesAdded = async (files: File[]) => {
    if (files.length > 0) {
      const pdfFile = await generateThumbnails(files[0])
      setFile(pdfFile)
      setRanges([{ id: crypto.randomUUID(), start: 1, end: pdfFile.pageCount }])
    }
  }

  const handleAddRange = () => {
    if (!file) return
    setRanges((prev) => [
      ...prev,
      { id: crypto.randomUUID(), start: 1, end: file.pageCount },
    ])
    setHasEditedRanges(true)
  }

  const handleRemoveRange = (id: string) => {
    setRanges((prev) => prev.filter((r) => r.id !== id))
  }

  const handleRangeChange = (
    id: string,
    field: "start" | "end",
    value: number,
  ) => {
    setRanges((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    )
    setHasEditedRanges(true)
  }

  const handleSplit = async () => {
    if (!file || ranges.length === 0) return
    if (!guardOperation()) return

    const blobs = await splitPdf(file.file, {
      ranges: ranges.map((r) => ({ start: r.start, end: r.end })),
    })

    blobs.forEach((blob, i) => {
      const filename = `${file.name.replace(".pdf", "")}_part${i + 1}.pdf`
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)

      void uploadDocument(blob, filename, "split_pdf")
    })

    markCompleted()
  }

  return (
    <>
      <SEO
        title="Split PDF Online - Free PDF Splitter | StitchPDF"
        description="Split PDF files into multiple documents or extract specific pages for free. No software installation required. Works in your browser."
        canonical="https://stitchpdf.com/split-pdf"
        ogTitle="Split PDF Online - StitchPDF"
        ogDescription="Split PDF files into multiple documents or extract specific pages for free."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Split PDF - StitchPDF",
            description:
              "Extract pages or split PDF into multiple files. Free online tool.",
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Split a PDF File",
            description:
              "Extract specific pages or split a PDF into multiple files.",
            step: [
              {
                "@type": "HowToStep",
                name: "Upload PDF",
                text: "Select the PDF file you want to split.",
              },
              {
                "@type": "HowToStep",
                name: "Choose pages",
                text: "Define page ranges to extract (e.g., 1-3, 5-10).",
              },
              {
                "@type": "HowToStep",
                name: "Download split files",
                text: "Click Split & Download to get your extracted pages.",
              },
            ],
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <Scissors className="text-primary h-8 w-8" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Split PDF</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              Extract pages or split PDF into multiple files
            </p>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              {howItWorks.map((step, i) => {
                const hasFile = file !== null
                const isComplete =
                  completed ||
                  (hasFile && i === 0) ||
                  (hasEditedRanges && i === 1) ||
                  (processing && i <= 1)
                const isActive =
                  !completed &&
                  ((processing && i === 2) ||
                    (!processing &&
                      ((hasFile && !hasEditedRanges && i === 1) ||
                        (hasFile && i === 2))))
                return (
                  <div
                    key={step.text}
                    className={`flex items-center gap-2 ${
                      isComplete
                        ? "text-green-700 dark:text-green-400"
                        : isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                        isComplete
                          ? "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-400"
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <step.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{step.text}</span>
                  </div>
                )
              })}
            </div>

            {!file ? (
              <div className="mt-8">
                <FileDropzone
                  onFilesAdded={(files) => void handleFilesAdded(files)}
                  multiple={false}
                />
              </div>
            ) : (
              <div className="mt-8">
                <div className="mb-4 flex items-center justify-end">
                  <p className="text-muted-foreground text-sm">
                    {remainingOperations} operations remaining today
                  </p>
                </div>

                <div className="flex items-start gap-6">
                  <div>
                    <PdfThumbnail
                      src={file.thumbnails[0]}
                      label={`${file.name} (${file.pageCount} pages)`}
                      onRemove={() => setFile(null)}
                    />
                  </div>

                  <div className="flex-1 space-y-4">
                    <h3 className="font-medium">Page Ranges</h3>

                    {ranges.map((range, i) => (
                      <div key={range.id} className="flex items-end gap-3">
                        <div>
                          <Label className="text-xs">From</Label>
                          <Input
                            type="number"
                            min={1}
                            max={file.pageCount}
                            value={range.start}
                            onChange={(e) =>
                              handleRangeChange(
                                range.id,
                                "start",
                                parseInt(e.target.value) || 1,
                              )
                            }
                            className="w-20"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">To</Label>
                          <Input
                            type="number"
                            min={1}
                            max={file.pageCount}
                            value={range.end}
                            onChange={(e) =>
                              handleRangeChange(
                                range.id,
                                "end",
                                parseInt(e.target.value) || 1,
                              )
                            }
                            className="w-20"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveRange(range.id)}
                          disabled={ranges.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <span className="text-muted-foreground pb-2 text-sm">
                          → part{i + 1}.pdf
                        </span>
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddRange}
                    >
                      <Plus className="mr-1 h-3 w-3" /> Add Range
                    </Button>
                  </div>
                </div>

                <div className="mt-8 flex gap-4">
                  <Button
                    onClick={() => void handleSplit()}
                    disabled={ranges.length === 0 || processing}
                    className="gap-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Splitting... {Math.round(progress)}%
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Split & Download
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {loadingThumbnails && (
              <div className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading PDF...
              </div>
            )}

            <RelatedTools currentTool="split-pdf" />

            <section className="mt-16 border-t pt-12">
              <h2 className="text-xl font-semibold">About This Tool</h2>
              <div className="text-muted-foreground mt-4 space-y-4 text-sm leading-relaxed">
                <p>
                  Define page ranges and extract them into separate PDFs. Add
                  multiple ranges to create several output files from one
                  document.
                </p>
                <p>
                  Processing happens in your browser. Confidential documents
                  stay on your device.
                </p>
                <h3 className="text-foreground pt-4 font-medium">Features</h3>
                <ul className="list-inside list-disc space-y-2">
                  <li>
                    <strong>Custom ranges:</strong> Extract pages 1-5, 10-15, or
                    any combination
                  </li>
                  <li>
                    <strong>Multiple outputs:</strong> Create several PDFs from
                    one source
                  </li>
                  <li>
                    <strong>Preview:</strong> See thumbnails before splitting
                  </li>
                  <li>
                    <strong>No upload:</strong> Runs locally in your browser
                  </li>
                </ul>
                <h3 className="text-foreground pt-4 font-medium">Use Cases</h3>
                <p>
                  Extract a chapter from an ebook. Separate invoices by client.
                  Pull specific forms from a document package. Share only the
                  pages someone needs.
                </p>
              </div>
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>

      <Modals />
    </>
  )
}
